import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Send, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Field, GradButton } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { TarihInput } from "@/components/TarihInput";
import { hesapla } from "@/components/cari/CariIslemler";
import { ekstreHtml, SECENEKLER } from "@/components/cari/ekstreHtml";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

const ADIMLAR = ["Ekstre Bilgileri", "Önizleme", "E-posta"];
const btn = "px-4 h-10 rounded-lg border border-white/15 text-sm hover:bg-white/5 inline-flex items-center gap-2";

const Adimlar = ({ adim }) => (
  <div className="flex items-center justify-center gap-3" data-testid="ekstre-adimlar">
    {ADIMLAR.map((a, i) => <div key={a} className="flex items-center gap-3">{i > 0 && <span className="w-10 h-px bg-white/20" />}
      <span data-testid={`ekstre-adim-${i}`} className={`px-5 py-2 rounded-lg text-sm font-semibold ${i === adim ? "bg-blue-600 text-white" : i < adim ? "bg-emerald-600 text-white" : "bg-white/5 text-muted-foreground"}`}>{a}</span></div>)}
  </div>
);

const Bilgiler = ({ o, set, pbler }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <Field label="Başlangıç Tarihi *"><TarihInput data-testid="ekstre-bas" className="h-10 bg-white/5" value={o.bas} onChange={(e) => set("bas", e.target.value)} /></Field>
    <Field label="Bitiş Tarihi *"><TarihInput data-testid="ekstre-bit" className="h-10 bg-white/5" value={o.bit} onChange={(e) => set("bit", e.target.value)} /></Field>
    <Field label="Para birimi *"><SelectBox testid="ekstre-pb" value={o.pb} onChange={(v) => set("pb", v)} options={[{ value: "TOPLAM", label: "Toplam (TRY)" }, ...pbler.map((p) => ({ value: p, label: p }))]} /></Field>
    <Field label="Sayfa Yönü"><SelectBox testid="ekstre-yon" value={o.yon} onChange={(v) => set("yon", v)} options={[{ value: "dikey", label: "Dikey" }, { value: "yatay", label: "Yatay" }]} /></Field>
    <Field label="Açıklama" className="sm:col-span-2"><textarea data-testid="ekstre-aciklama" rows={3} className="w-full rounded-md border border-input bg-white/5 px-3 py-2 text-sm" value={o.aciklama} onChange={(e) => set("aciklama", e.target.value)} /></Field>
    <div className="sm:col-span-2 font-semibold">Görüntüleme Seçenekleri</div>
    {SECENEKLER.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid={`ekstre-sec-${k}`} checked={o.sec[k]} onCheckedChange={(v) => set("sec", { ...o.sec, [k]: !!v })} />{l}</label>)}
  </div>
);

const Eposta = ({ m, setM }) => (
  <div className="grid gap-4 max-w-xl mx-auto w-full">
    <Field label="Alıcı e-posta *"><Input data-testid="ekstre-eposta-to" type="email" className="bg-white/5" value={m.to} onChange={(e) => setM({ ...m, to: e.target.value })} /></Field>
    <Field label="Konu"><Input data-testid="ekstre-eposta-konu" className="bg-white/5" value={m.konu} onChange={(e) => setM({ ...m, konu: e.target.value })} /></Field>
    <Field label="Mesaj"><textarea data-testid="ekstre-eposta-mesaj" rows={4} className="w-full rounded-md border border-input bg-white/5 px-3 py-2 text-sm" value={m.mesaj} onChange={(e) => setM({ ...m, mesaj: e.target.value })} /></Field>
    <p className="text-xs text-muted-foreground">Önizlemedeki ekstre e-postanın içeriği olarak gönderilir.</p>
  </div>
);

export const EkstreYazdirDialog = ({ cari, ekstre, kapat }) => {
  const { ayar } = useAuth();
  const ilk = ekstre.satirlar.find((r) => r.tarih)?.tarih || new Date().toISOString().slice(0, 10);
  const [o, setO] = useState({ bas: ilk, bit: new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" }), pb: "TOPLAM", yon: "dikey", aciklama: "",
    sec: Object.fromEntries(SECENEKLER.map(([k]) => [k, true])) });
  const [adim, setAdim] = useState(0);
  const [m, setM] = useState({ to: cari.email || "", konu: `${ayar?.kisa_ad || ayar?.unvan || ""} · Cari hesap ekstresi`, mesaj: "" });
  const [busy, setBusy] = useState(false);
  const ifr = useRef(null);
  const set = (k, v) => setO((x) => ({ ...x, [k]: v }));
  const pbler = Object.keys(ekstre.risk.bakiyeler || { TRY: 0 });
  const html = useMemo(() => {
    const tum = hesapla(ekstre.satirlar.filter((r) => o.sec.kurfarki || !/kur fark/i.test(r.tip || r.aciklama || "")), o.pb);
    const once = tum.filter((r) => r.tarih < o.bas);
    return ekstreHtml({ firma: ayar || {}, cari, rows: tum.filter((r) => r.tarih >= o.bas && r.tarih <= o.bit), devir: once.length ? once[once.length - 1].gbak : 0, o });
  }, [o, ekstre, cari, ayar]);
  const yeniSekme = () => { const w = window.open("", "_blank"); if (w) { w.document.write(html); w.document.close(); } };
  const yazdir = () => (adim === 1 && ifr.current ? ifr.current.contentWindow.print() : yeniSekme());
  const gonder = async () => {
    setBusy(true);
    try { const r = await api.post(`/cariler/${cari.id}/ekstre-eposta`, { ...m, html }); toast.success(`Ekstre ${r.data.adres} adresine gönderildi`); kapat(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && kapat()}>
      <DialogContent className="glass max-w-5xl max-h-[94vh] overflow-y-auto space-y-4" data-testid="ekstre-yazdir-dialog">
        <DialogHeader><DialogTitle className="font-head">Cari Ekstresi Oluştur</DialogTitle></DialogHeader>
        <Adimlar adim={adim} />
        {adim === 0 && <Bilgiler o={o} set={set} pbler={pbler} />}
        {adim === 1 && <iframe ref={ifr} title="Ekstre önizleme" data-testid="ekstre-onizleme" srcDoc={html} className="w-full h-[62vh] rounded-lg bg-white" />}
        {adim === 2 && <Eposta m={m} setM={setM} />}
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={kapat} className={btn} data-testid="ekstre-kapat">Kapat</button>
          {adim > 0 && <button onClick={() => setAdim(adim - 1)} className={btn} data-testid="ekstre-onceki"><ArrowLeft className="w-4 h-4" />Önceki</button>}
          {adim < 2 && <button onClick={yazdir} className={btn} data-testid="ekstre-yazdir"><Printer className="w-4 h-4" />Yazdır</button>}
          {adim === 1 && <button onClick={yeniSekme} className={btn} data-testid="ekstre-yeni-sekme"><ExternalLink className="w-4 h-4" />Yeni Sekmede Aç</button>}
          {adim === 0 && <GradButton data-testid="ekstre-olustur" disabled={!o.bas || !o.bit} onClick={() => setAdim(1)}>Oluştur</GradButton>}
          {adim === 1 && <GradButton data-testid="ekstre-eposta-adim" onClick={() => setAdim(2)}><Send className="w-4 h-4 mr-1 inline" />E-posta Gönder</GradButton>}
          {adim === 2 && <GradButton data-testid="ekstre-eposta-gonder" disabled={busy || !m.to.includes("@")} onClick={gonder}>{busy ? "Gönderiliyor…" : "Gönder"}</GradButton>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
